import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/api";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import InitialLoader from "@/components/InitialLoader";
import CustomCursor from "@/components/CustomCursor";  // disabled since Phase 7C — see App() body

// Public pages
import Home from "@/pages/Home";
// CursusList is now used inside ExpertisePA wrapper (route /expertise-pa)
import CursusDetailPage from "@/pages/cursus/CursusDetailPage";
import FormationDetail from "@/pages/FormationDetail";
import Sessions from "@/pages/Sessions";
import PriseEnCharge from "@/pages/PriseEnCharge";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Inscription from "@/pages/Inscription";
import InscriptionSuccess from "@/pages/InscriptionSuccess";
import NotFound from "@/pages/NotFound";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CguPlaceholder from "@/pages/CguPlaceholder";
import NosFormateurs from "@/pages/NosFormateurs";
import NotreComiteScientifique from "@/pages/NotreComiteScientifique";
import ExpertisePA from "@/pages/ExpertisePA";
import DiversificationActivite from "@/pages/DiversificationActivite";
import ModeleEconomique from "@/pages/ModeleEconomique";
import ActualitePAList from "@/pages/ActualitePAList";
import ActualitePADetail from "@/pages/ActualitePADetail";
import ParlerConseiller from "@/pages/ParlerConseiller";

// Public Phase 7B Lot 3
import Indicateurs from "@/pages/public/Indicateurs";
import Reclamation from "@/pages/public/Reclamation";
import Accessibilite from "@/pages/public/Accessibilite";
import ReglementInterieurPage from "@/pages/public/ReglementInterieurPage";
import LegalAckModal from "@/components/LegalAckModal";
import ConstructionGate from "@/components/ConstructionGate";
import ConstructionPage from "@/pages/ConstructionPage";

// Admin
import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDossier from "@/pages/admin/AdminUserDossier";
import AdminInscriptions from "@/pages/admin/AdminInscriptions";
import AdminQualiopi from "@/pages/admin/AdminQualiopi";
import AdminPrevisionnel from "@/pages/admin/AdminPrevisionnel";
import AdminReclamations from "@/pages/admin/AdminReclamations";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminSessionWizard from "@/pages/admin/AdminSessionWizard";
import AdminPlanning from "@/pages/admin/AdminPlanning";
import AdminEmailLogs from "@/pages/admin/AdminEmailLogs";
import AdminCursus from "@/pages/admin/AdminCursus";
import AdminFormations from "@/pages/admin/AdminFormations";
import AdminSessions from "@/pages/admin/AdminSessions";
import AdminSalles from "@/pages/admin/AdminSalles";
import AdminComiteScientifique from "@/pages/admin/AdminComiteScientifique";
import AdminActualites from "@/pages/admin/AdminActualites";
import AdminConseillerRequests from "@/pages/admin/AdminConseillerRequests";

// Formateur
import FormateurLayout from "@/components/formateur/FormateurLayout";
import FormateurDashboard from "@/pages/formateur/FormateurDashboard";
import Onboarding from "@/pages/formateur/Onboarding";
import ProgrammeEditPage from "@/pages/formateur/ProgrammeEditPage";
import DisponibilitesEditPage from "@/pages/formateur/DisponibilitesEditPage";
import VillesEditPage from "@/pages/formateur/VillesEditPage";
import FormationsEditPage from "@/pages/formateur/FormationsEditPage";
import ProfilPublicEditPage from "@/pages/formateur/ProfilPublicEditPage";
import FormateurSessions from "@/pages/formateur/FormateurSessions";
import FormateurSessionDetail from "@/pages/formateur/FormateurSessionDetail";
import FormateurEvaluations from "@/pages/formateur/FormateurEvaluations";
import EvaluationEditor from "@/pages/formateur/EvaluationEditor";
import FormateurBilan from "@/pages/formateur/FormateurBilan";

// Stagiaire
import StagiaireLayout from "@/components/stagiaire/StagiaireLayout";
import StagiaireDashboard from "@/pages/stagiaire/StagiaireDashboard";
import StagiaireCursusList from "@/pages/stagiaire/StagiaireCursusList";
import StagiaireCursusDetail from "@/pages/stagiaire/StagiaireCursusDetail";
import StagiaireSessions from "@/pages/stagiaire/StagiaireSessions";
import StagiaireSessionDetail from "@/pages/stagiaire/StagiaireSessionDetail";
import StagiaireDocuments from "@/pages/stagiaire/StagiaireDocuments";
import StagiaireProfile from "@/pages/stagiaire/StagiaireProfile";
import StagiaireContact from "@/pages/stagiaire/StagiaireContact";
import StagiaireEvaluation from "@/pages/stagiaire/StagiaireEvaluation";

function AdminPlaceholderPage({ label }) {
    return (
        <div className="mx-auto max-w-4xl px-6 py-16 text-center">
            <h1 className="font-display text-4xl">{label}</h1>
            <p className="mt-4 text-brand-smoke">Bientôt disponible en Phase 4.</p>
        </div>
    );
}

export default function App() {
    return (
        <div className="App font-sans antialiased text-brand-ink">
            <InitialLoader />
            {/* Phase 7C — CustomCursor désactivé. Cursor natif système partout. */}
            {false && <CustomCursor />}
            <BrowserRouter>
                <AuthProvider>
                    <LegalAckModal />
                    <Routes>
                        {/* Public site — gated by construction mode */}
                        <Route element={<ConstructionGate><Layout /></ConstructionGate>}>
                            <Route path="/" element={<Home />} />
                            <Route path="/cursus" element={<Navigate to="/expertise-pa" replace />} />
                            <Route path="/expertise-pa" element={<ExpertisePA />} />
                            <Route path="/expert-prevention-active" element={<Navigate to="/diversification-activite" replace />} />
                            <Route path="/diversification-activite" element={<DiversificationActivite />} />
                            <Route path="/modele-economique" element={<ModeleEconomique />} />
                            <Route path="/actualite-pa" element={<ActualitePAList />} />
                            <Route path="/actualite-pa/:slug" element={<ActualitePADetail />} />
                            <Route path="/cursus/therapie-manuelle" element={<Navigate to="/cursus/fondations" replace />} />
                            <Route path="/cursus/reeducation-sportive" element={<Navigate to="/cursus/application" replace />} />
                            <Route path="/cursus/neurologie-geriatrie" element={<Navigate to="/cursus/maitrise" replace />} />
                            <Route path="/cursus/:slug" element={<CursusDetailPage />} />
                            <Route path="/formations/:slug" element={<FormationDetail />} />
                            <Route path="/sessions" element={<Sessions />} />
                            <Route path="/prise-en-charge" element={<PriseEnCharge />} />
                            <Route path="/a-propos" element={<About />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/parler-conseiller" element={<ParlerConseiller />} />
                            <Route path="/indicateurs" element={<Indicateurs />} />
                            <Route path="/reclamation" element={<Reclamation />} />
                            <Route path="/accessibilite" element={<Accessibilite />} />
                            <Route path="/reglement-interieur" element={<ReglementInterieurPage />} />
                            <Route path="/nos-formateurs" element={<NosFormateurs />} />
                            <Route path="/notre-comite-scientifique" element={<NotreComiteScientifique />} />
                            <Route path="/inscription/:sessionId" element={<Inscription />} />
                            <Route path="/inscription/success" element={<InscriptionSuccess />} />
                            <Route path="/cgu" element={<CguPlaceholder />} />
                        </Route>

                        {/* Signup is also gated (public sign-up) */}
                        <Route path="/signup" element={<ConstructionGate><Signup /></ConstructionGate>} />

                        {/* Direct preview of the construction page */}
                        <Route path="/construction" element={<ConstructionPage />} />
                        {/* Login + password reset (no chrome, not gated) */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />

                        {/* Formateur onboarding */}
                        <Route
                            path="/formateur/onboarding"
                            element={
                                <ProtectedRoute roles={["formateur"]}>
                                    <Onboarding />
                                </ProtectedRoute>
                            }
                        />

                        {/* Formateur space */}
                        <Route
                            element={
                                <ProtectedRoute roles={["formateur"]}>
                                    <RequireOnboarding>
                                        <FormateurLayout />
                                    </RequireOnboarding>
                                </ProtectedRoute>
                            }
                        >
                            <Route path="/formateur" element={<FormateurDashboard />} />
                            <Route path="/formateur/profil-public" element={<ProfilPublicEditPage />} />
                            <Route path="/formateur/programme" element={<ProgrammeEditPage />} />
                            <Route path="/formateur/disponibilites" element={<DisponibilitesEditPage />} />
                            <Route path="/formateur/villes" element={<VillesEditPage />} />
                            <Route path="/formateur/formations" element={<FormationsEditPage />} />
                            <Route path="/formateur/sessions" element={<FormateurSessions />} />
                            <Route path="/formateur/sessions/:id" element={<FormateurSessionDetail />} />
                            <Route path="/formateur/sessions/:id/bilan" element={<FormateurBilan />} />
                            <Route path="/formateur/evaluations" element={<FormateurEvaluations />} />
                            <Route path="/formateur/evaluations/:formation_slug/:eval_type" element={<EvaluationEditor />} />
                        </Route>

                        {/* Admin */}
                        <Route
                            element={
                                <ProtectedRoute roles={["admin"]}>
                                    <AdminLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route path="/admin" element={<AdminDashboard />} />
                            <Route path="/admin/utilisateurs" element={<AdminUsers />} />
                            <Route path="/admin/utilisateurs/:id/dossier" element={<AdminUserDossier />} />
                            <Route path="/admin/inscriptions" element={<AdminInscriptions />} />
                            <Route path="/admin/cursus" element={<AdminCursus />} />
                            <Route path="/admin/formations" element={<AdminFormations />} />
                            <Route path="/admin/sessions" element={<AdminSessions />} />
                            <Route path="/admin/sessions/nouvelle" element={<AdminSessionWizard />} />
                            <Route path="/admin/planning" element={<AdminPlanning />} />
                            <Route path="/admin/email-logs" element={<AdminEmailLogs />} />
                            <Route path="/admin/salles" element={<AdminSalles />} />
                            <Route path="/admin/comite-scientifique" element={<AdminComiteScientifique />} />
                            <Route path="/admin/actualites" element={<AdminActualites />} />
                            <Route path="/admin/qualiopi" element={<AdminQualiopi />} />
                            <Route path="/admin/previsionnel" element={<AdminPrevisionnel />} />
                            <Route path="/admin/reclamations" element={<AdminReclamations />} />
                            <Route path="/admin/demandes-conseiller" element={<AdminConseillerRequests />} />
                            <Route path="/admin/parametres" element={<AdminSettings />} />
                        </Route>

                        {/* Stagiaire */}
                        <Route
                            element={
                                <ProtectedRoute roles={["stagiaire"]}>
                                    <StagiaireLayout />
                                </ProtectedRoute>
                            }
                        >
                            <Route path="/stagiaire" element={<StagiaireDashboard />} />
                            <Route path="/stagiaire/cursus" element={<StagiaireCursusList />} />
                            <Route path="/stagiaire/cursus/:slug" element={<StagiaireCursusDetail />} />
                            <Route path="/stagiaire/sessions" element={<StagiaireSessions />} />
                            <Route path="/stagiaire/sessions/:id" element={<StagiaireSessionDetail />} />
                            <Route path="/stagiaire/documents" element={<StagiaireDocuments />} />
                            <Route path="/stagiaire/profil" element={<StagiaireProfile />} />
                            <Route path="/stagiaire/contact" element={<StagiaireContact />} />
                            <Route path="/stagiaire/evaluations/:template_id" element={<StagiaireEvaluation />} />
                        </Route>

                        {/* Fallback 404 — also gated */}
                        <Route element={<ConstructionGate><Layout /></ConstructionGate>}>
                            <Route path="*" element={<NotFound />} />
                        </Route>
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
            <Toaster richColors position="top-right" />
        </div>
    );
}

function RequireOnboarding({ children }) {
    const { user } = useAuth();
    if (user && (user.must_change_password || !user.onboarding_completed)) {
        return <Navigate to="/formateur/onboarding" replace />;
    }
    return children;
}
